import { useEffect, useRef } from 'react';

// build/version.json 을 주기적으로 폴링하여 새 배포를 감지한다.
// dev 모드에서는 version.json 이 없으므로 fetch 가 실패하고 조용히 비활성화된다.

const VERSION_FILE = '/version.json';
const POLL_INTERVAL_MS = 3 * 60 * 1000; // 3분
const FIRST_FETCH_DELAY_MS = 5 * 1000; // 마운트 직후 5초 뒤 첫 폴링 (초기 로드와 충돌 방지)

async function fetchVersion() {
  try {
    const res = await fetch(`${VERSION_FILE}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * @param {() => void} onNewVersion 새 버전이 감지되었을 때 호출 (한 번 감지 후 추가 호출 안 함)
 */
export function useVersionCheck(onNewVersion) {
  const initialVersionRef = useRef(null);
  const notifiedRef = useRef(false);
  const callbackRef = useRef(onNewVersion);
  callbackRef.current = onNewVersion;

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const check = async () => {
      const current = await fetchVersion();
      if (cancelled || current == null) return;

      if (initialVersionRef.current == null) {
        initialVersionRef.current = current;
        return;
      }

      if (current !== initialVersionRef.current && !notifiedRef.current) {
        notifiedRef.current = true;
        callbackRef.current?.();
      }
    };

    // 마운트 직후 한 번 (초기 버전 캐시)
    const firstTimeoutId = setTimeout(check, FIRST_FETCH_DELAY_MS);
    intervalId = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(firstTimeoutId);
      clearInterval(intervalId);
    };
  }, []);
}
